import { useMemo } from 'react';
import guideMarkdown from '../../content/agencyOnboardingGuide.md?raw';
import { parseAgencyOnboardingGuide } from '../../content/parseAgencyOnboardingGuide';
import AgencyOnboardingGuideDoc from '../../content/AgencyOnboardingGuideDoc';

/**
 * Agency onboarding: video embed + written walkthrough.
 * Set `VITE_AGENCY_ONBOARDING_EMBED_URL` at build time (e.g. embed URL from your video host).
 * Written guide images are served from `/onboarding/*`.
 */
export default function AgencyGuidePage() {
  const embedSrc = (import.meta.env.VITE_AGENCY_ONBOARDING_EMBED_URL as string | undefined)?.trim() || '';

  const guide = useMemo(() => parseAgencyOnboardingGuide(guideMarkdown), []);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-auto p-4 md:p-8">
      <div className="mx-auto w-full max-w-4xl space-y-8 pb-12">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-glass-text md:text-3xl">Getting started</h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-glass-text-2 md:text-base">
            Watch the onboarding video, then scroll through the step-by-step guide with screenshots. You can pause
            and replay the video any time—your work in myKOL is saved in the app separately.
          </p>
        </div>

        <section aria-labelledby="onboarding-video-title">
          <h2 id="onboarding-video-title" className="mb-3 text-lg font-semibold text-glass-text">
            Video walkthrough
          </h2>
          <div className="overflow-hidden rounded-xl border border-glass bg-glass-2 shadow-sm">
            <div className="aspect-video w-full bg-black">
              {embedSrc ? (
                <iframe
                  src={embedSrc}
                  title="Agency onboarding video"
                  className="h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                />
              ) : (
                <div className="flex h-full min-h-[200px] flex-col items-center justify-center gap-2 px-4 text-center text-sm text-glass-text-2">
                  <p>No onboarding video URL is configured for this deployment.</p>
                  <p className="text-xs text-glass-text-3">
                    Set <code className="rounded bg-glass px-1 py-0.5 text-glass-text">VITE_AGENCY_ONBOARDING_EMBED_URL</code>{' '}
                    at build time to show the walkthrough player.
                  </p>
                </div>
              )}
            </div>
          </div>
          <p className="mt-2 text-xs text-glass-text-3">
            Tip: use fullscreen on the player for a clearer view. If the video does not load, check your network or
            corporate firewall settings.
          </p>
        </section>

        <section aria-labelledby="onboarding-written-title">
          <h2 id="onboarding-written-title" className="mb-2 text-lg font-semibold text-glass-text">
            Written guide &amp; screenshots
          </h2>
          <p className="mb-6 text-sm text-glass-text-2">
            Follow these steps end-to-end on the platform. Click any image to view it full screen; press Escape or
            use the close control to dismiss.
          </p>
          <AgencyOnboardingGuideDoc guide={guide} />
        </section>
      </div>
    </div>
  );
}
